/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * GlobalSearchBar — Spotlight-style search with autocomplete, keyboard nav
 * Appears in header, searches vendors/venues/guests/checklist/budget
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import searchService from '@services/searchService';
import { useAuth } from '@hooks/useAuth';

const TYPE_META = {
  vendor: {
    icon: 'bi-shop', color: '#9B30FF',
    // Vendor detail is /vendors/:category/:slug (2 segments). A 1-segment
    // URL collides with /vendors/:category and 404s. Fall back to the
    // public listing filtered by name when we don't have a slug.
    route: (s) => {
      if (s.slug) {
        const cat = (typeof s.category === 'object' ? s.category?.slug : null) || 'all';
        return `/vendors/${cat}/${s.slug}`;
      }
      return `/vendors?q=${encodeURIComponent(s.label || s.businessName || s.name || '')}`;
    },
  },
  venue: {
    icon: 'bi-building', color: '#3B82F6',
    // Venue detail is /venues/:id (Venue model has no slug column).
    // Fall back to a filtered list URL when id is also missing.
    route: (s) => s.id ? `/venues/${s.id}` : `/venues?q=${encodeURIComponent(s.label || s.name || '')}`,
  },
  category: { icon: 'bi-tag', color: '#F59E0B', route: (s) => `/vendors?category=${s.slug}` },
  guest: { icon: 'bi-person', color: '#22C55E', route: () => '/dashboard/guests' },
  checklist: { icon: 'bi-check2-square', color: '#6366F1', route: () => '/dashboard/checklist' },
  budget: { icon: 'bi-wallet2', color: '#EF4444', route: () => '/dashboard/budget' }
};

const GlobalSearchBar = ({ variant = 'header' }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const role = user?.role || 'couple';

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + K to open
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); setResults(null); return; }
    setLoading(true);
    try {
      const [sugRes, globalRes] = await Promise.all([
        searchService.suggestions(q),
        searchService.global({ q, limit: 3 })
      ]);
      setSuggestions(sugRes.data?.data?.suggestions || []);
      setResults(globalRes.data?.data || null);
    } catch(e) {}
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIdx(-1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
    if (val.length >= 2) setOpen(true);
  };

  // Navigate to result
  const goTo = (item) => {
    const meta = TYPE_META[item.type];
    if (meta) navigate(meta.route(item));
    setOpen(false);
    setQuery('');
  };

  // Modules to jump to from a typed keyword. Role-aware so a coordinator
  // typing "calendar" lands on /coordinator/calendar instead of the
  // couple-only /dashboard/day-timeline, and "checklist" doesn't dump
  // a coordinator into a 404 or empty page that doesn't belong to them.
  //
  // Order matters: longest/specific matches first.
  const COUPLE_KEYWORDS = [
    { match: /^(seating|table)/i,         to: '/dashboard/seating-chart' },
    { match: /^(budget|expense)/i,        to: '/dashboard/budget' },
    { match: /^(checklist|task)/i,        to: '/dashboard/checklist' },
    { match: /^(guest|rsvp)/i,            to: '/dashboard/guests' },
    { match: /^(timeline|day.timeline)/i, to: '/dashboard/day-timeline' },
    { match: /^(invitation|invite)/i,     to: '/dashboard/invitations' },
    { match: /^(photo|gallery)/i,         to: '/dashboard/photo-wall' },
    { match: /^(vendor|booking)/i,        to: '/vendors' },
    { match: /^(venue|location)/i,        to: '/venues' },
    { match: /^(setting|profile|account)/i, to: '/dashboard/settings' },
  ];
  const COORDINATOR_KEYWORDS = [
    { match: /^(wedding|couple|client)/i,    to: '/coordinator/weddings' },
    { match: /^(calendar|event|timeline)/i,  to: '/coordinator/calendar' },
    { match: /^(vendor|network)/i,           to: '/coordinator/vendors' },
    { match: /^(multi|portfolio|dashboard)/i,to: '/coordinator/multi-dashboard' },
    { match: /^(white.?label|brand)/i,       to: '/coordinator/white-label' },
    { match: /^(notification|alert)/i,       to: '/coordinator/notifications' },
    { match: /^(setting|profile|account)/i,  to: '/coordinator/settings' },
  ];
  const VENDOR_KEYWORDS = [
    { match: /^(quote|inquiry|lead)/i,       to: '/vendor/quotes' },
    { match: /^(booking|client)/i,           to: '/vendor/bookings' },
    { match: /^(calendar|schedule|availability)/i, to: '/vendor/calendar' },
    { match: /^(photo|portfolio|gallery)/i,  to: '/vendor/profile' },
    { match: /^(message|chat|reply)/i,       to: '/vendor/messages' },
    { match: /^(setting|profile|account)/i,  to: '/vendor/settings' },
  ];
  const ADMIN_KEYWORDS = [
    { match: /^(user|customer)/i,            to: '/admin/users' },
    { match: /^(vendor)/i,                   to: '/admin/vendors' },
    { match: /^(venue)/i,                    to: '/admin/venues' },
    { match: /^(subscription|payment|revenue)/i, to: '/admin/subscription-lifecycle' },
    { match: /^(email|deliverability)/i,     to: '/admin/email-deliverability' },
  ];

  const keywordsForRole = () => {
    if (role === 'coordinator') return COORDINATOR_KEYWORDS;
    if (role === 'vendor')      return VENDOR_KEYWORDS;
    if (role === 'admin' || role === 'super_admin') return ADMIN_KEYWORDS;
    return COUPLE_KEYWORDS;
  };

  // Where the "Search all for X" button should drop the user when no
  // keyword matched. Couples → public marketplace (their main discovery
  // funnel). All other roles get scoped to their own listing so we don't
  // bounce them out of their portal into the public site.
  const fallbackTargetForRole = (q) => {
    const encoded = encodeURIComponent(q);
    if (role === 'coordinator') return `/coordinator/weddings?q=${encoded}`;
    if (role === 'vendor')      return `/vendor/quotes?q=${encoded}`;
    if (role === 'admin' || role === 'super_admin') return `/admin?q=${encoded}`;
    return `/vendors?q=${encoded}`;
  };

  // Full search: route by module keyword if matched, else fall back to
  // the role's home listing.
  const fullSearch = () => {
    if (query.length < 2) return;
    const moduleHit = keywordsForRole().find(m => m.match.test(query.trim()));
    if (moduleHit) {
      // Module keyword: navigate but also append ?q= so the destination
      // page can pre-fill its own search/filter UI if it supports it.
      const sep = moduleHit.to.includes('?') ? '&' : '?';
      navigate(`${moduleHit.to}${sep}q=${encodeURIComponent(query)}`);
    } else {
      navigate(fallbackTargetForRole(query));
    }
    setOpen(false);
  };

  // Keyboard navigation
  const allItems = [...suggestions];
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(prev => Math.min(prev + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(prev => Math.max(prev - 1, -1)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && allItems[activeIdx]) goTo(allItems[activeIdx]);
      else if (query.length >= 2) fullSearch();
    }
  };

  const hasContent = suggestions.length > 0 || results;

  return (
    <div ref={ref} style={{ position: 'relative', width: variant === 'page' ? '100%' : 280 }}>
      {/* Input */}
      <div style={{ position: 'relative' }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: '0.85rem' }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.length >= 2 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={variant === 'page' ? t('common.search') + ' vendors, venues, cities...' : (navigator.userAgent?.includes('Mac') ? '⌘K ' : 'Ctrl+K ') + t('common.search') + '...'}
          style={{
            width: '100%', padding: '8px 12px 8px 36px', border: '1px solid #E5E7EB',
            borderRadius: 24, fontSize: '0.82rem', outline: 'none', background: '#F9FAFB',
            transition: 'all 0.2s',
            ...(open ? { borderColor: '#9B30FF', boxShadow: '0 0 0 3px rgba(155,48,255,0.1)', background: '#fff' } : {})
          }}
        />
        {loading && <i className="bi bi-arrow-repeat" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', animation: 'spin 1s linear infinite', color: '#9B30FF', fontSize: '0.8rem' }} />}
      </div>

      {/* Dropdown */}
      {open && hasContent && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, minWidth: 320,
          background: '#fff', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          zIndex: 9999, maxHeight: 420, overflowY: 'auto', border: '1px solid #F3F4F6'
        }}>
          {/* Autocomplete Suggestions */}
          {suggestions.length > 0 && (
            <div style={{ padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <div className="px-3 pb-1" style={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Suggestions</div>
              {suggestions.map((s, i) => {
                const meta = TYPE_META[s.type];
                return (
                  <div key={`${s.type}-${s.id}`} onClick={() => goTo(s)}
                    className="d-flex align-items-center gap-2 px-3 py-2"
                    style={{
                      cursor: 'pointer', fontSize: '0.82rem',
                      background: activeIdx === i ? '#F8F4FF' : 'transparent',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={() => setActiveIdx(i)}>
                    <i className={`bi ${meta?.icon}`} style={{ color: meta?.color, fontSize: '0.9rem', minWidth: 20 }} />
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-medium" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.label}</div>
                      {s.sub && <small className="text-muted">{s.sub}</small>}
                    </div>
                    <span className="badge" style={{ fontSize: '0.55rem', background: `${meta?.color}15`, color: meta?.color }}>{s.type}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Full Results Summary */}
          {results && Object.keys(results).some(k => results[k]?.length > 0) && (
            <div style={{ padding: '8px 0' }}>
              {Object.entries(results).map(([section, items]) => {
                if (!items?.length) return null;
                const sectionMeta = TYPE_META[section.replace(/s$/, '')] || TYPE_META[section] || {};
                return (
                  <div key={section}>
                    <div className="px-3 py-1 d-flex align-items-center gap-1" style={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase' }}>
                      <i className={`bi ${sectionMeta.icon || 'bi-search'}`} style={{ fontSize: '0.6rem' }} />
                      {section} ({items.length})
                    </div>
                    {items.slice(0, 3).map(item => (
                      <div key={item.id} className="d-flex align-items-center gap-2 px-3 py-1"
                        style={{ cursor: 'pointer', fontSize: '0.78rem' }}
                        onClick={() => goTo({ type: section.replace(/s$/, ''), ...item })}
                        onMouseEnter={e => { e.currentTarget.style.background = '#F8F4FF'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                        <span className="text-truncate">{item.businessName || item.name || item.firstName + ' ' + (item.lastName || '') || item.title || item.itemName}</span>
                        {item.city && <small className="text-muted ms-auto flex-shrink-0">{item.city}</small>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Full Search Link */}
          <div className="px-3 py-2" style={{ borderTop: '1px solid #F3F4F6' }}>
            <button onClick={fullSearch} className="btn btn-sm w-100" style={{ background: '#F8F4FF', color: '#9B30FF', fontSize: '0.75rem', fontWeight: 600, borderRadius: 8 }}>
              <i className="bi bi-search me-1" />Search all for "{query}"
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
